import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Carousel } from 'antd';
import { FaStar, FaQuoteLeft, FaGraduationCap, FaUniversity, FaSchool } from 'react-icons/fa';

const Testimonials = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const testimonials = [
        {
            quote: "The AI-powered automation and analytics completely transformed our administration! We've reduced manual work by 80% and our staff is much more productive.",
            name: "Principal Kaur",
            designation: "Delhi Public School",
            rating: 5,
            image: "assets/images/about1.jpeg",
            icon: <FaSchool />
        },
        {
            quote: "Fee management is now completely headache-free, and our teachers absolutely love the intuitive interface. Best investment we've made for our institution!",
            name: "Admin Ms. Das",
            designation: "St. Xavier's College",
            rating: 5,
            image: "assets/images/WhatWeOffer.jpeg",
            icon: <FaUniversity />
        },
        {
            quote: "Our data is secure, parents are much happier with real-time communication features, and student engagement has increased significantly since we implemented Educational Eternity.",
            name: "Director Sharma",
            designation: "Modern Academy",
            rating: 5,
            image: "assets/images/newabout.jpg",
            icon: <FaGraduationCap />
        },
        {
            quote: "The AI mentorship feature is a game-changer. It provides personalized insights that help us guide students more effectively and identify learning gaps early.",
            name: "Dr. Patel",
            designation: "Elite Educational Institute",
            rating: 5,
            image: "assets/images/Aboutnew.jpeg",
            icon: <FaSchool />
        },
        {
            quote: "The online exam system has made conducting exams so much easier. The anti-cheating measures and instant result generation save us countless hours.",
            name: "Prof. Kumar",
            designation: "Tech College",
            rating: 5,
            image: "assets/images/about12.jpeg",
            icon: <FaUniversity />
        },
        {
            quote: "Attendance tracking with biometric integration has eliminated all manual work. The automated SMS alerts to parents keep everyone informed.",
            name: "Principal Singh",
            designation: "Global School",
            rating: 5,
            image: "assets/images/newai.jpeg",
            icon: <FaSchool />
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>Testimonials - Educational Eternity | Client Success Stories</title>
                <meta name="description" content="Read testimonials from educational institutions using Educational Eternity. See how we're transforming schools and colleges across India." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        What Our Clients Say
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Trusted by leading educational institutions across India
                    </p>
                </div>
            </section>

            {/* Testimonials Carousel */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <Carousel 
                            autoplay 
                            autoplaySpeed={5000} 
                            dots={true} 
                            className="testimonial-carousel"
                            effect="fade"
                        >
                            {testimonials.map((testimonial, idx) => (
                                <div key={idx} className="px-4">
                                    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
                                        <div className="flex flex-col md:flex-row items-center gap-8">
                                            {/* Image/Icon */}
                                            <div className="flex-shrink-0">
                                                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-5xl shadow-lg">
                                                    {testimonial.icon}
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 text-center md:text-left">
                                                <div className="flex justify-center md:justify-start mb-4">
                                                    {[...Array(testimonial.rating)].map((_, i) => (
                                                        <FaStar key={i} className="text-yellow-400 text-2xl mx-1" />
                                                    ))}
                                                </div>
                                                <FaQuoteLeft className="text-blue-200 text-4xl mb-4 mx-auto md:mx-0" />
                                                <p className="text-xl md:text-2xl text-gray-700 italic mb-6 leading-relaxed">
                                                    "{testimonial.quote}"
                                                </p>
                                                <div>
                                                    <p className="text-lg font-bold text-gray-900">{testimonial.name}</p>
                                                    <p className="text-gray-600">{testimonial.designation}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Carousel>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12" data-aos="fade-up">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Trusted by Institutions Nationwide
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Join hundreds of educational institutions already using Educational Eternity
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                        {[
                            { number: "20+", label: "Institutions" },
                            { number: "55K+", label: "Active Users" },
                            { number: "98%", label: "Satisfaction Rate" },
                            { number: "24/7", label: "Support" }
                        ].map((stat, idx) => (
                            <div 
                                key={idx} 
                                data-aos="fade-up" 
                                data-aos-delay={idx * 100}
                                className="text-center"
                            >
                                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-gray-600 font-semibold">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Testimonials;

